import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  Camera, UploadSimple, CheckCircle, XCircle, MapPin, Briefcase, FileArrowUp,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminBulkPhotos — drop a pile of job photos → auto-attach every image to
 * one Work Order. Perfect for weekly turn photo dumps.
 *
 * Flow: pick property address → pick WO at that address → choose bucket
 *       (before / after / receipt) → drop N photos → uploads in parallel.
 *
 * Route: /admin/bulk-photos  (admin-gated)
 */
export default function AdminBulkPhotos() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState("");        // free-text with datalist
  const [wos, setWos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [target, setTarget] = useState("");           // "wo:<id>" or "project:<id>"
  const [kind, setKind] = useState("before");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [lastRun, setLastRun] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Load address list once
  useEffect(() => {
    axios.get(`${BASE}/api/whatsapp/property-picker`, auth())
      .then(r => setAddresses(r.data.addresses || []))
      .catch(() => toast.error("Couldn't load properties"));
    // eslint-disable-next-line
  }, []);

  // When user finishes typing an address (matches an entry in the datalist
  // OR they hit blur with a non-empty value), pull BOTH WOs and Projects at
  // that address so they can attach photos to a Project Radar item too.
  const lookupTargets = async (addr) => {
    const a = (addr || "").trim();
    if (!a || a.length < 3) { setWos([]); setProjects([]); setTarget(""); return; }
    try {
      const [wr, pr] = await Promise.all([
        axios.get(`${BASE}/api/whatsapp/wos-at-address`, { ...auth(), params: { address: a } })
          .catch(() => ({ data: { wos: [] } })),
        axios.get(`${BASE}/api/projects/lookup/at-address`, { ...auth(), params: { address: a } })
          .catch(() => ({ data: { projects: [] } })),
      ]);
      const wos_ = wr.data.wos || [];
      const projs_ = pr.data.projects || [];
      setWos(wos_);
      setProjects(projs_);
      // Auto-select the newest active WO OR first active project
      const activeWo = wos_.find(w => !["completed", "canceled", "merged"].includes(w.status));
      const activeProj = projs_.find(p => p.status !== "closed");
      if (activeWo) setTarget(`wo:${activeWo.id}`);
      else if (activeProj) setTarget(`project:${activeProj.id}`);
      else setTarget("");
    } catch (e) {
      toast.error("Couldn't load jobs / projects at that address");
    }
  };
  // Trigger lookup as soon as the typed address matches a known property
  useEffect(() => {
    if (!address) { setWos([]); setProjects([]); setTarget(""); return; }
    const known = addresses.some(a => a.address === address);
    if (!known) return;
    lookupTargets(address);
    // eslint-disable-next-line
  }, [address, addresses]);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    if (target.startsWith("project:")) {
      // Project Radar photo endpoint — kind is a query param, kinds are
      // before | after | other (no "receipt" — we degrade to "other").
      const projId = target.slice("project:".length);
      const projKind = kind === "receipt" ? "other" : kind;
      return axios.post(
        `${BASE}/api/projects/${projId}/photos?kind=${encodeURIComponent(projKind)}`,
        fd,
        {
          headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
          timeout: 120000, signal: abortRef.current?.signal,
        },
      );
    }
    // Fallback / default → WO
    const woId = target.startsWith("wo:") ? target.slice("wo:".length) : target;
    fd.append("kind", kind);
    return axios.post(`${BASE}/api/jobs/${woId}/photo`, fd, {
      headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
      timeout: 120000, signal: abortRef.current?.signal,
    });
  };

  const handleFiles = async (fileList) => {
    if (!target) {
      toast.error("Pick a Work Order or Project first");
      return;
    }
    const files = Array.from(fileList).filter(
      f => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (files.length === 0) {
      toast.error("No image or video files in that batch");
      return;
    }
    setUploading(true);
    setLastRun(null);
    setProgress({ done: 0, total: files.length, failed: 0 });
    abortRef.current = new AbortController();

    // Concurrent upload with cap so we don't nuke the network
    const CONCURRENCY = 3;
    const failures = [];
    let i = 0;
    const runOne = async () => {
      while (i < files.length) {
        const idx = i++;
        const f = files[idx];
        try {
          await uploadOne(f);
          setProgress(p => ({ ...p, done: p.done + 1 }));
        } catch (e) {
          failures.push({ name: f.name, error: e?.response?.data?.detail || e.message });
          setProgress(p => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
        }
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, runOne));
    setUploading(false);
    const ok = files.length - failures.length;
    setLastRun({ ok, failed: failures.length, failures, target });
    if (ok > 0) toast.success(`Uploaded ${ok} photo(s)`);
    if (failures.length) toast.error(`${failures.length} upload(s) failed`);
  };

  const cancelUpload = () => abortRef.current?.abort();

  // Native drag-drop support — the whole page becomes a drop target so you
  // can just yank the folder onto the tab instead of hunting for the button.
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  useEffect(() => {
    const onDragEnter = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounter.current += 1;
      setDragging(true);
    };
    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
    };
    const onDragLeave = (e) => {
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); }
    };
    const onDrop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounter.current = 0;
      setDragging(false);
      if (!target) {
        toast.error("Pick an address and Work Order or Project first, then drop again");
        return;
      }
      handleFiles(e.dataTransfer.files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, kind]);

  const targetLabel = (() => {
    if (target.startsWith("wo:")) {
      const w = wos.find(x => x.id === target.slice(3));
      if (!w) return null;
      return { kind: "WO", label: `WO ${w.work_order_number || w.id.slice(-4)} — ${w.title || ""}`, id: w.id, href: `/admin/jobs/${w.id}` };
    }
    if (target.startsWith("project:")) {
      const p = projects.find(x => x.id === target.slice(8));
      if (!p) return null;
      return { kind: "Project", label: `${p.name}${p.unit ? " · " + p.unit : ""}`, id: p.id, href: `/admin/projects/${p.id}` };
    }
    return null;
  })();

  return (
    <AdminShell>
      {dragging && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center bg-cyan-500/20 backdrop-blur-sm border-8 border-dashed border-cyan-500"
          data-testid="drag-overlay">
          <div className="bg-white px-8 py-6 border-4 border-cyan-500 shadow-2xl text-center">
            <FileArrowUp weight="fill" size={64} className="mx-auto text-cyan-600"/>
            <div className="mt-3 font-black uppercase tracking-widest text-xl text-cyan-800">
              Drop to upload
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {targetLabel
                ? `Photos will attach to ${targetLabel.kind}: ${targetLabel.label} as ${kind}`
                : "Pick an address and Work Order or Project first"}
            </div>
          </div>
        </div>
      )}
      <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="bulk-photos-page">
        <div className="flex items-center gap-3">
          <Camera weight="fill" size={30} className="text-cyan-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Bulk Photos → WO or Project
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Type an address → we surface every Work Order AND Project Radar item at that property. Pick one and drop your photos.
            </div>
          </div>
        </div>

        {/* Address + target pickers */}
        <div className="mt-6 p-4 border-2 border-slate-200 bg-white space-y-3">
          {/* Address typeahead */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1">
              <MapPin weight="fill" size={12} className="text-cyan-600"/> Property address
            </label>
            <input
              type="text"
              list="fr-bulk-addresses"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => lookupTargets(address)}
              placeholder={`Type — e.g. 240 Hermitage (${addresses.length} on file)`}
              data-testid="bulk-address-input"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"
            />
            <datalist id="fr-bulk-addresses">
              {addresses.slice(0, 300).map(a => (
                <option key={a.address} value={a.address}>
                  {a.active > 0 ? `${a.active} open` : `${a.total} total`}
                </option>
              ))}
            </datalist>
          </div>

          {/* Target picker — WOs + Projects merged */}
          {address && (wos.length > 0 || projects.length > 0) && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1">
                <Briefcase weight="fill" size={12} className="text-cyan-600"/>
                What at this property? ({wos.length} WO{wos.length === 1 ? "" : "s"} · {projects.length} Project{projects.length === 1 ? "" : "s"})
              </label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="bulk-target-picker"
                className="mt-1 h-10 px-2 border-2 border-slate-300 text-sm w-full"
              >
                <option value="">— Pick a Work Order or Project —</option>
                {wos.length > 0 && (
                  <optgroup label="🎫 Work Orders">
                    {wos.map(w => (
                      <option key={`wo:${w.id}`} value={`wo:${w.id}`}>
                        WO {w.work_order_number || w.id.slice(-4)} · {w.status || "?"} · {(w.title || "").slice(0, 60)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {projects.length > 0 && (
                  <optgroup label="🧱 Project Radar">
                    {projects.map(p => (
                      <option key={`project:${p.id}`} value={`project:${p.id}`}>
                        {p.name || `Project ${p.id.slice(-4)}`} · {p.status || "draft"}{p.unit ? " · " + p.unit : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {targetLabel && (
                <a href={targetLabel.href} target="_blank" rel="noreferrer"
                  className="mt-1 inline-block text-[11px] text-cyan-700 hover:underline">
                  Open {targetLabel.kind} in new tab →
                </a>
              )}
            </div>
          )}
          {address && wos.length === 0 && projects.length === 0 && (
            <div className="p-2 text-xs text-slate-500 italic">
              No Work Orders or Projects at this address yet. Try another address or create a WO first.
            </div>
          )}

          {/* Kind picker */}
          {target && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                Where do these belong?
              </label>
              <div className="mt-1 flex gap-2 flex-wrap">
                {[
                  { k: "before",  label: "Before Photos",  color: "amber" },
                  { k: "after",   label: "After Photos",   color: "emerald" },
                  { k: "receipt", label: "Receipts",       color: "fuchsia" },
                ].map(o => (
                  <button key={o.k} onClick={() => setKind(o.k)}
                    data-testid={`bulk-kind-${o.k}`}
                    className={`h-9 px-3 border-2 font-black uppercase text-[10px] tracking-widest transition ${
                      kind === o.k
                        ? `bg-${o.color}-600 border-${o.color}-700 text-white`
                        : `bg-white border-slate-300 text-slate-700 hover:border-slate-500`
                    }`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {target.startsWith("project:") && kind === "receipt" && (
                <div className="mt-1 text-[10px] text-amber-700">
                  Projects don't have a Receipts bucket — we'll file it as "Other".
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drop zone */}
        <div className="mt-6">
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !target}
            data-testid="bulk-drop-btn"
            className={`w-full h-40 border-4 border-dashed flex flex-col items-center justify-center transition disabled:opacity-40 ${
              uploading ? "border-slate-300 bg-slate-100"
              : target ? "border-cyan-400 bg-cyan-50 hover:border-cyan-600 hover:bg-cyan-100"
              : "border-slate-300 bg-slate-50"
            }`}
          >
            {uploading ? (
              <>
                <UploadSimple weight="bold" size={40} className="text-slate-500 animate-pulse"/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-slate-600">
                  Uploading… {progress.done} of {progress.total}
                </div>
                {progress.failed > 0 && (
                  <div className="mt-1 text-xs text-red-600 font-bold">
                    {progress.failed} failed so far
                  </div>
                )}
              </>
            ) : (
              <>
                <FileArrowUp weight="fill" size={44} className={target ? "text-cyan-600" : "text-slate-400"}/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-slate-800">
                  {target ? "Drop photos here" : "Pick a WO or Project first"}
                </div>
                <div className="mt-1 text-[11px] text-slate-600">or click to browse — multi-select up to hundreds at once</div>
              </>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            data-testid="bulk-file-input"
            onChange={(e) => { if (e.target.files?.length) { handleFiles(e.target.files); e.target.value = ""; }}}
          />
          {uploading && (
            <button onClick={cancelUpload}
              data-testid="bulk-cancel-btn"
              className="mt-2 w-full h-9 border-2 border-red-400 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] tracking-widest">
              Cancel upload
            </button>
          )}
        </div>

        {/* Last-run results */}
        {lastRun && (
          <div className="mt-6 p-4 border-2 border-slate-900 bg-white" data-testid="bulk-result">
            <div className="flex items-center gap-3 flex-wrap">
              <CheckCircle weight="fill" size={20} className="text-emerald-600"/>
              <div className="font-black uppercase tracking-widest text-sm text-slate-900">
                Attached {lastRun.ok} photo(s)
                {targetLabel ? ` to ${targetLabel.kind}` : ""}
              </div>
              {targetLabel && (
                <a href={targetLabel.href} target="_blank" rel="noreferrer"
                  className="text-xs font-black uppercase tracking-widest text-cyan-700 hover:underline">
                  Open {targetLabel.kind} →
                </a>
              )}
              {lastRun.failed > 0 && (
                <span className="text-xs font-black uppercase tracking-widest text-red-600 ml-auto">
                  {lastRun.failed} failed
                </span>
              )}
            </div>
            {lastRun.failures.length > 0 && (
              <div className="mt-3 divide-y divide-slate-100 border-2 border-red-200">
                {lastRun.failures.map((f, i) => (
                  <div key={i} className="p-2 text-xs flex items-center gap-3">
                    <XCircle size={14} className="text-red-500 flex-none"/>
                    <span className="font-mono truncate flex-1">{f.name}</span>
                    <span className="text-red-600">{f.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
